/**
 * Keyframe interpolation.
 *
 *   computeAt(layer, t) → Layer
 *
 * Given the layer and the current project time, return a synthetic Layer
 * whose fields reflect the interpolated keyframe state. The result is fed
 * straight into the existing engine.renderLayer so entry / exit transitions
 * still apply on top.
 *
 * Per spec 2026-04-29-keyframes-design.md.
 */

import type { Easing, Keyframe, Layer } from '../types';

export function computeAt(layer: Layer, t: number): Layer {
  const kfs = layer.keyframes;
  if (!kfs || kfs.length === 0) return layer;

  // Collect every leaf path that any keyframe touches.
  const touched = new Set<string>();
  for (const kf of kfs) collectLeafPaths(kf.values, '', touched);
  if (touched.size === 0) return layer;

  // Deep-clone the layer so we can mutate it without affecting the store.
  const out = deepClone(layer);

  for (const path of touched) {
    const { prev, next } = neighbours(kfs, path, t);
    if (!prev && !next) continue;
    if (prev && !next) {
      setPath(out, path, getPath(prev.values, path));
      continue;
    }
    if (!prev && next) {
      // Before the first keyframe — hold the first defined value so the
      // change is observable when playback reaches it.
      setPath(out, path, getPath(next.values, path));
      continue;
    }
    if (prev && next) {
      const span = next.t - prev.t;
      const u = span > 0 ? (t - prev.t) / span : 1;
      const eased = ease(u, next.ease);
      const a = getPath(prev.values, path);
      const b = getPath(next.values, path);
      setPath(out, path, lerp(a, b, eased));
    }
  }
  return out;
}

// ─── path utilities ───
function collectLeafPaths(value: unknown, prefix: string, into: Set<string>): void {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    if (prefix) into.add(prefix);
    return;
  }
  if (typeof value === 'object') {
    let any = false;
    for (const k of Object.keys(value as Record<string, unknown>)) {
      any = true;
      collectLeafPaths((value as Record<string, unknown>)[k], prefix ? `${prefix}.${k}` : k, into);
    }
    if (!any && prefix) into.add(prefix);
    return;
  }
  if (prefix) into.add(prefix);
}

function getPath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function setPath(obj: object, path: string, value: unknown): void {
  if (value === undefined) return;
  const parts = path.split('.');
  let cur = obj as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const p = parts[i] as string;
    const next = cur[p];
    if (typeof next !== 'object' || next === null) {
      cur[p] = {};
    }
    cur = cur[p] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1] as string] = value;
}

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

// ─── neighbour search ───
function neighbours(
  kfs: Keyframe[],
  path: string,
  t: number,
): { prev: Keyframe | null; next: Keyframe | null } {
  let prev: Keyframe | null = null;
  let next: Keyframe | null = null;
  for (const kf of kfs) {
    if (getPath(kf.values, path) === undefined) continue;
    if (kf.t <= t) {
      if (!prev || kf.t > prev.t) prev = kf;
    } else {
      if (!next || kf.t < next.t) next = kf;
    }
  }
  return { prev, next };
}

// ─── easing ───
function ease(u: number, e: Easing): number {
  const x = Math.max(0, Math.min(1, u));
  switch (e) {
    case 'linear':
      return x;
    case 'ease-in':
      return x * x * x;
    case 'ease-out':
      return 1 - Math.pow(1 - x, 3);
    case 'ease-in-out':
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }
}

// ─── value interpolation ───
function lerp(a: unknown, b: unknown, u: number): unknown {
  if (typeof a === 'number' && typeof b === 'number') {
    return a + (b - a) * u;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    if (isHexColor(a) && isHexColor(b)) return lerpHexColor(a, b, u);
    // step — switch to b once we cross the next keyframe (u >= 1)
    return u >= 1 ? b : a;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return u >= 1 ? b : a;
  // Step everything else (arrays, objects we couldn't decompose, etc.).
  return u >= 1 ? b : a;
}

function isHexColor(s: string): boolean {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(s);
}

function lerpHexColor(a: string, b: string, u: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const r = Math.round(ar + (br - ar) * u);
  const g = Math.round(ag + (bg - ag) * u);
  const bl = Math.round(ab + (bb - ab) * u);
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

function parseHex(s: string): [number, number, number] {
  const v = s.replace('#', '');
  const expanded =
    v.length === 3 ? v.split('').map((c) => c + c).join('') : v;
  const n = parseInt(expanded, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}

// ─── helpers used by store ───

/** Insert or replace the keyframe at the given t for a layer. */
export function upsertKeyframe(
  list: Keyframe[] | undefined,
  next: Keyframe,
): Keyframe[] {
  const arr = list ? [...list] : [];
  const idx = arr.findIndex((k) => Math.abs(k.t - next.t) < 1);
  if (idx >= 0) {
    arr[idx] = { ...arr[idx]!, ...next, values: { ...(arr[idx]!.values), ...next.values } };
  } else {
    arr.push(next);
  }
  arr.sort((a, b) => a.t - b.t);
  return arr;
}
